import { formatNumber, formatPercent, formatPricePerSqm, formatEuros } from "@/lib/explore/utils/format-helpers"

export interface CommuneData {
  partMaisons21?: number
  niveauVie21?: number
  partLogementsVacants21?: number
  partLogementSocial?: number
  partResidencesPrincipales21?: number
  partProprietaires21?: number
  prixMoyenMaison?: number
  prixMoyenAppartement?: number
  prixMoyenLocaux?: number
  prixMoyen?: number
  nombresVentesMaison?: number
  nombresVentesAppartement?: number
  nombresVentesLocaux?: number
  nombresVentes?: number
  mayor?: {
    firstName?: string
    lastName?: string
    electionDate?: string
    politicalParty?: string
  }
  zonePinel?: string
}

interface CommuneInfoPopoverProps {
  open: boolean
  onClose: () => void
  selectedCommune: CommuneData | null
}

export const CommuneInfoPopover = ({ open, onClose, selectedCommune }: CommuneInfoPopoverProps) => {
  if (!open || !selectedCommune) return null

  return (
    <>
      <div className="fixed inset-0 z-20" onClick={onClose} />
      <div
        className="absolute top-0 left-full ml-2 z-30 bg-white/95 backdrop-blur-sm map-panel-shadow rounded-xl w-80 max-h-[70vh] flex flex-col anim-scale-in"
        style={{ transformOrigin: "top left" }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 rounded-t-lg shrink-0">
          <span className="text-sm font-medium text-gray-700">Details de la commune</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">
            &times;
          </button>
        </div>
        <div className="px-4 py-3 space-y-3 overflow-y-auto scrollbar-thin">
          {/* INSEE */}
          <div>
            <h4 className="text-sm font-bold text-primary-light uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              INSEE (2021)
            </h4>
            <div className="bg-primary-lighter/40 rounded p-2 space-y-1 text-sm border border-primary/10">
              <Row
                label="Maisons"
                value={selectedCommune.partMaisons21 != null ? formatPercent(selectedCommune.partMaisons21) : "\u2014"}
              />
              <Row
                label="Revenu median"
                value={selectedCommune.niveauVie21 != null ? formatEuros(selectedCommune.niveauVie21) : "\u2014"}
              />
              <Row
                label="Logements vacants"
                value={
                  selectedCommune.partLogementsVacants21 != null
                    ? formatPercent(selectedCommune.partLogementsVacants21)
                    : "\u2014"
                }
              />
              <Row
                label="Logements sociaux"
                value={
                  selectedCommune.partLogementSocial != null
                    ? formatPercent(selectedCommune.partLogementSocial)
                    : "\u2014"
                }
              />
              <Row
                label="Residences principales"
                value={
                  selectedCommune.partResidencesPrincipales21 != null
                    ? formatPercent(selectedCommune.partResidencesPrincipales21)
                    : "\u2014"
                }
              />
              <Row
                label="Proprietaires"
                value={
                  selectedCommune.partProprietaires21 != null
                    ? formatPercent(selectedCommune.partProprietaires21)
                    : "\u2014"
                }
              />
            </div>
          </div>

          {/* DVF */}
          <div>
            <h4 className="text-sm font-bold text-success uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-success" />
              DVF - Prix moyen
            </h4>
            <div className="bg-success-lighter/20 rounded p-2 space-y-1 text-sm border border-success/10">
              <Row
                label="Maisons"
                value={
                  selectedCommune.prixMoyenMaison != null
                    ? formatPricePerSqm(selectedCommune.prixMoyenMaison)
                    : "\u2014"
                }
              />
              <Row
                label="Appartements"
                value={
                  selectedCommune.prixMoyenAppartement != null
                    ? formatPricePerSqm(selectedCommune.prixMoyenAppartement)
                    : "\u2014"
                }
              />
              <Row
                label="Locaux"
                value={
                  selectedCommune.prixMoyenLocaux != null
                    ? formatPricePerSqm(selectedCommune.prixMoyenLocaux)
                    : "\u2014"
                }
              />
              <Row
                label="Global"
                value={selectedCommune.prixMoyen != null ? formatPricePerSqm(selectedCommune.prixMoyen) : "\u2014"}
              />
            </div>
          </div>

          {/* DVF - Ventes */}
          <div>
            <h4 className="text-sm font-bold text-success uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-success" />
              DVF - Ventes
            </h4>
            <div className="bg-success-lighter/20 rounded p-2 space-y-1 text-sm border border-success/10">
              <Row
                label="Maisons"
                value={
                  selectedCommune.nombresVentesMaison != null
                    ? formatNumber(selectedCommune.nombresVentesMaison)
                    : "\u2014"
                }
              />
              <Row
                label="Appartements"
                value={
                  selectedCommune.nombresVentesAppartement != null
                    ? formatNumber(selectedCommune.nombresVentesAppartement)
                    : "\u2014"
                }
              />
              <Row
                label="Locaux"
                value={
                  selectedCommune.nombresVentesLocaux != null
                    ? formatNumber(selectedCommune.nombresVentesLocaux)
                    : "\u2014"
                }
              />
              <Row
                label="Total"
                value={selectedCommune.nombresVentes != null ? formatNumber(selectedCommune.nombresVentes) : "\u2014"}
              />
            </div>
          </div>

          {/* Maire */}
          {selectedCommune.mayor && (
            <div>
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                Maire
              </h4>
              <div className="bg-gray-100 rounded p-2 text-sm">
                <div className="font-medium text-gray-800">
                  {selectedCommune.mayor.firstName} {selectedCommune.mayor.lastName}
                </div>
                <div className="text-gray-500 text-sm">
                  {selectedCommune.mayor.electionDate &&
                    `Elu en ${new Date(selectedCommune.mayor.electionDate).getFullYear()}`}
                  {selectedCommune.mayor.politicalParty && ` \u2014 ${selectedCommune.mayor.politicalParty}`}
                </div>
              </div>
            </div>
          )}

          {/* Pinel */}
          {selectedCommune.zonePinel && (
            <div>
              <h4 className="text-sm font-bold text-warning-dark uppercase tracking-wider mb-1 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-warning" />
                Zone Pinel
              </h4>
              <div className="bg-warning-lighter/30 rounded p-2 text-sm font-medium text-gray-800 border border-warning/10">
                {selectedCommune.zonePinel}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between">
    <span className="text-gray-600">{label}</span>
    <span className="font-medium text-gray-800">{value}</span>
  </div>
)
